// Personalization delta card — Scoring Rationale tab (storefront hub design).

import React from 'react';
import { Sparkles } from 'lucide-react';

export interface PriorityTheme {
  slug: string;
  label: string;
  activeInDelta: boolean;
}

export interface PersonalizationDelta {
  archetype: string;
  archetypeSlug?: string;
  activePriorities: string[];
  relevanceTier?: string;
  intensityTier?: string;
  bvDelta: number;
  easeDelta: number;
  contractVersion?: string;
}

export interface PersonalizationDeltaCardProps {
  delta: PersonalizationDelta;
  themes?: PriorityTheme[];
  /** When true, shows the “Illustrative” chip (demo / non-warehouse path). */
  illustrative?: boolean;
}

const sign = (n: number) => (n > 0 ? `+${n}` : `${n}`);

export function PersonalizationDeltaCard({
  delta,
  themes,
  illustrative = false,
}: PersonalizationDeltaCardProps) {
  const unselected = (themes || []).filter((t) => !t.activeInDelta);

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="w-4 h-4 text-[#FF6E4C]" />
        <span className="text-sm font-bold text-[#18365E]">Personalization delta</span>
        {illustrative && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 uppercase tracking-wide">
            Illustrative
          </span>
        )}
        {delta.contractVersion && (
          <span className="text-[10px] text-[#717182] ml-auto">{delta.contractVersion}</span>
        )}
      </div>

      <p className="text-xs text-[#4b5563] leading-relaxed mt-2">
        Profiled as a <span className="font-semibold text-[#18365E]">{delta.archetype}</span> client
        {delta.archetypeSlug ? (
          <>
            {' '}
            (<span className="font-mono text-[10px]">{delta.archetypeSlug}</span> archetype)
          </>
        ) : null}
        {delta.relevanceTier != null && delta.intensityTier != null && (
          <>
            . Relevance tier {delta.relevanceTier} · intensity tier {delta.intensityTier}.
          </>
        )}
      </p>

      {delta.activePriorities.length > 0 && (
        <div className="mt-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#717182]">
            Active priorities
          </p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {delta.activePriorities.map((p) => (
              <span
                key={p}
                className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#18365E] text-white border border-[#18365E]"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {unselected.length > 0 && (
        <div className="mt-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#717182]">
            Unselected priority themes
          </p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {unselected.map((t) => (
              <span
                key={t.slug}
                className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white text-[#717182] border border-[#e5e7eb]"
              >
                {t.label}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[#717182]">
          Adjustments vs. baseline scoring
        </p>
        <div className="flex gap-4 mt-1 flex-wrap">
          <div
            className={`rounded-lg px-3 py-2 border ${
              delta.bvDelta >= 0
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-rose-50 border-rose-200'
            }`}
          >
            <p
              className={`text-sm font-bold ${
                delta.bvDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'
              }`}
            >
              Business value {sign(delta.bvDelta)}{' '}
              <span className="font-semibold text-[#717182]">vs. baseline</span>
            </p>
          </div>
          <div
            className={`rounded-lg px-3 py-2 border ${
              delta.easeDelta >= 0
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-rose-50 border-rose-200'
            }`}
          >
            <p
              className={`text-sm font-bold ${
                delta.easeDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'
              }`}
            >
              Implementation ease {sign(delta.easeDelta)}{' '}
              <span className="font-semibold text-[#717182]">vs. baseline</span>
            </p>
          </div>
        </div>
        <p className="text-[10px] text-[#717182] leading-relaxed mt-1.5">
          Adjustments reflect this client&apos;s profile; final dimension scores shown below already
          include them.
        </p>
      </div>
    </div>
  );
}

export default PersonalizationDeltaCard;
